// IM-XPR-002 (IM-UI-098, IM-UX-110/113) — Demais objetivos:
//   • Rever minhas ofertas: a J4 (12 etapas, motor intacto) em cinco macrofases (Mapear → Cobrir → Comparar → Priorizar →
//     Decidir), uma ação principal por fase e a síntese do portfólio antes dos detalhes.
//   • Encontrar empresas para vender: desde a IM-XPR-006 em TargetsJourney.tsx (três intenções dentro do objetivo).
//   • Planejar meu próximo ano e Acompanhar meu plano: desde a IM-JRN-015/016 em PlanObjective.tsx e TrackObjective.tsx.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { J4_PORTFOLIO_V1, STEP_STATE_LABEL, objectiveOf, phaseStatusOf, stepDeps, type JourneyDto, type ObjectiveKey, type PortfolioListItemDto } from '@intelimove/contracts';
import { AsyncView } from '../components/States';
import { portfolioApi } from '../data/portfolio';
import { useResource } from '../hooks/useResource';
import { useExperience } from './ExperienceContext';
import { loadObjectiveJourney, phaseConsequence, phaseHeadline, phasesOf, plural, stepPlain } from './data';
import { ObjectiveFlow, type FlowPhase } from './ObjectiveFlow';
import { ObjectiveHead, openStepOf, ProcessOverview, StartButton, StepsDetail, usePhase } from './ObjectiveWorkspace';
import { ContextLink } from './returnTo';
import { OfferMapping } from './OfferMapping';

const J4_DEPS = stepDeps(J4_PORTFOLIO_V1);

const DONE = new Set(['CONCLUIDA', 'CONCLUIDA_COM_LIMITACOES', 'DESATUALIZADA']);

// ── Rever minhas ofertas (J4) ────────────────────────────────────────────────────────────────────────────────────
export function OffersObjective() {
  const xp = useExperience();
  const { state, reload, refresh } = useResource(async (s) => {
    const [jr, list] = await Promise.all([
      loadObjectiveJourney('ofertas', xp.journeys, s).catch(() => ({ catalog: null, journey: null })),
      portfolioApi.list(s).then((x) => x.items).catch(() => [] as PortfolioListItemDto[]),
    ]);
    return { journey: jr.journey, portfolio: list.filter((x) => x.status === 'ACTIVE').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] ?? null };
  }, [xp.journeys]);
  return (
    <section aria-labelledby="page-title">
      <AsyncView state={state} onRetry={reload} empty={null} loading="metrics">
        {(d) => <JourneyObjective objective="ofertas" journey={d.journey} reload={reload} refresh={refresh} journeysOn={xp.journeys} aside={<PortfolioSynthesis p={d.portfolio} />} />}
      </AsyncView>
    </section>
  );
}

function PortfolioSynthesis({ p }: { p: PortfolioListItemDto | null }) {
  const s = p?.summary;
  const lines = s ? [
    s.offerings ? `${plural(s.offerings, 'oferta analisada', 'ofertas analisadas')}.` : null,
    s.confirmedGaps || s.gaps ? `${plural(s.confirmedGaps || s.gaps, 'lacuna de cobertura', 'lacunas de cobertura')}${s.confirmedGaps ? ' confirmada' + (s.confirmedGaps > 1 ? 's' : '') : ''}.` : null,
    s.overlapsProblem ? `${plural(s.overlapsProblem, 'sobreposição que pede atenção', 'sobreposições que pedem atenção')}.` : null,
    s.recommendations ? `${plural(s.recommendations, 'recomendação', 'recomendações')}${s.highPriority ? `, ${s.highPriority} de alta prioridade` : ''}.` : null,
    s.priceSeries ? `${plural(s.priceSeries, 'série de preço comparável', 'séries de preço comparáveis')}.` : null,
    s.opportunities ? `${plural(s.opportunities, 'oportunidade', 'oportunidades')} de pacote ou oferta de entrada.` : null,
  ].filter(Boolean) : [];
  return (
    <aside className="xp-panel" aria-labelledby="xp-syn-t">
      <span className="xp-kicker">Síntese do InteliMove</span>
      <h3 id="xp-syn-t">Seu portfólio</h3>
      {p && lines.length ? <><p className="cell-sub">Análise “{p.name}”, versão {p.versionNo}.</p><ul className="plain-list">{lines.map((l) => <li key={l}>{l}</li>)}</ul><Link className="link-btn" to={`/portfolio/${p.id}`}>Abrir a análise</Link></>
        : <p className="xp-quiet">Ainda não há análise de portfólio. Ela nasce das ofertas confirmadas no contexto da empresa, sem pesquisa nova.</p>}
    </aside>
  );
}

/** Objetivo conduzido por jornada publicada: fluxo, painel por fase com as etapas em linguagem de tela e a ação principal. */
function JourneyObjective({ objective, journey, reload, refresh, journeysOn, aside }: { objective: ObjectiveKey; journey: JourneyDto | null; reload: () => void; refresh: () => void; journeysOn: boolean; aside: ReactNode }) {
  const def = objectiveOf(objective)!;
  const phases = journey ? phasesOf(def, journey) : null;
  const current = phases?.find((p) => p.current);
  const flow: FlowPhase[] = def.phases.map((p) => {
    const v = phases?.find((x) => x.key === p.key);
    const open = journey ? p.steps.map((id) => journey.steps.find((s) => s.stepId === id)).filter((s) => s && s.state !== 'DISPENSADA' && s.kind !== 'OPCIONAL' && !DONE.has(s.state)).length : 0;
    return { key: p.key, name: p.name, question: p.question, state: v?.state ?? null, current: !!v?.current, status: journey ? phaseStatusOf(v, phases, J4_DEPS) : null,
      count: v && v.decisions ? plural(v.decisions, 'decisão aberta', 'decisões abertas') : open ? plural(open, 'passo aberto', 'passos abertos') : null };
  });
  const [selected, select] = usePhase(def.phases.map((p) => p.key), current?.key ?? def.phases[0]!.key);
  const view = phases?.find((p) => p.key === selected) ?? null;
  const ph = def.phases.find((p) => p.key === selected)!;
  const back = { to: `/objetivos/${objective}?fase=${selected}`, label: def.title };
  const step = openStepOf(journey, ph.steps);
  const steps = journey ? ph.steps.map((id) => journey.steps.find((s) => s.stepId === id)).filter((s): s is JourneyDto['steps'][number] => !!s) : [];
  const status = flow.find((f) => f.key === selected)?.status ?? null;
  // IM-XPR-005: o mapeamento oferta × perfil acontece aqui, na fase Mapear (não editando ICP por ICP).
  const mapping = objective === 'ofertas' && selected === 'mapear';
  return (
    <>
      <ObjectiveHead def={def} journey={journey}>
        {journey && <ContextLink className="secondary btn-link" to={`/jornadas/${journey.journey.id}`} back={back}>Ver etapas detalhadas</ContextLink>}
      </ObjectiveHead>
      <div className="xp-shell">
        <ProcessOverview phases={phases} journey={journey} extra={!journey && journeysOn ? <StartButton def={def} onStarted={reload} /> : null} />
        <ObjectiveFlow id={`xp-${objective}`} label={`Fases para ${def.title.toLowerCase()}`} phases={flow} selected={selected} onSelect={select}>
          <article className="xp-panel" aria-labelledby="xp-phase-t">
            <span className="xp-kicker">{status ? `Fase ${def.phases.indexOf(ph) + 1} de ${def.phases.length} · ${status.label}${status.detail ? ` · ${status.detail}` : ''}` : `Fase ${def.phases.indexOf(ph) + 1} de ${def.phases.length}`}</span>
            <h2 id="xp-phase-t">{view?.current ? phaseHeadline(objective, view, {}) : ph.question}</h2>
            <p>{view?.current ? phaseConsequence(phases!, view) ?? ph.result : `Ao final: ${ph.result}`}</p>
            {status?.block && (
              <div className="xp-block" role="note">
                <b>Fase bloqueada.</b> <span>{status.block.why}</span>
                <span><b>O que fazer:</b> {status.block.action}</span>
                <span>{status.block.unlocks}</span>
                {status.block.waitingFor && def.phases.find((x) => x.name === status.block!.waitingFor) && <button type="button" className="link-btn" onClick={() => select(def.phases.find((x) => x.name === status.block!.waitingFor)!.key)}>Ir para a fase {status.block.waitingFor}</button>}
              </div>
            )}
            {mapping && <OfferMapping back={back} onChanged={refresh} onDone={() => select('cobrir')} />}
            {mapping ? null : steps.length > 0 ? (
              <ul className="xp-list" role="list">
                {steps.filter((s) => s.state !== 'DISPENSADA').slice(0, 5).map((s) => (
                  <li key={s.stepId} className="xp-row">
                    <span className="xp-check" aria-hidden="true">{DONE.has(s.state) ? '✓' : '·'}</span>
                    <div className="xp-row-main"><b>{stepPlain(s.stepId, s.title)}</b><span>{STEP_STATE_LABEL[s.state]}{s.artifact ? ` · ${s.artifact.label}` : ''}{s.kind === 'OPCIONAL' ? ' · opcional' : ''}</span>
                      {s.missing.filter((m) => m.indispensable).slice(0, 2).map((m) => <span key={m.key}>Falta: {m.route ? <ContextLink className="link-btn" to={m.route} back={back}>{m.label}</ContextLink> : m.label}</span>)}</div>
                  </li>
                ))}
              </ul>
            ) : <p className="xp-quiet">{journey ? 'Nada a fazer nesta fase agora.' : 'Comece o objetivo para o InteliMove mostrar o que já está pronto em cada fase.'}</p>}
            {!mapping && !status?.block && step?.href && <div className="xp-cta-row"><ContextLink className="xp-primary petrol" to={step.href} back={back} aria-label={`${step.actionLabel ?? 'Abrir'}: ${stepPlain(step.stepId, step.title)}`}>{step.actionLabel ?? 'Abrir'}</ContextLink></div>}
            <StepsDetail journey={journey} ids={ph.steps} back={back} />
          </article>
          {aside}
        </ObjectiveFlow>
      </div>
    </>
  );
}
